
export interface FallacyDefinition {
  name: string;
  alternativeNames: string[];
  description: string;
  examples: string[];
  antidote: string;
  category: string;
  severity: string;
}

const FALLACY_DATA: Record<string, FallacyDefinition> = {
  "appeal-to-authority": {
    name: "Appeal to Authority",
    alternativeNames: ["Argument from Authority", "Argument from False Authority"],
    description: "Asserting that a claim is true simply because an authority or expert said it was, without providing further evidence.",
    examples: ["Dr. Smith says this vaccine is safe, so it must be.", "A famous actor endorses this diet, so it must work for weight loss."],
    antidote: "Verify the authority's expertise in the relevant field and consider other evidence.",
    category: "Appeal",
    severity: "Medium",
  },
  "appeal-to-emotion": {
    name: "Appeal to Emotion",
    alternativeNames: ["Argument from Emotion", "Playing on Emotions"],
    description: "Manipulating an emotional response in place of a valid or compelling argument.",
    examples: ["Think of all the children! We must ban this book!", "If you loved me, you would support my decision."],
    antidote: "Separate the emotional appeal from the actual evidence and reasoning.",
    category: "Appeal",
    severity: "Medium",
  },
  "appeal-to-fear": {
    name: "Appeal to Fear",
    alternativeNames: ["Argument from Fear", "Scare Tactics"],
    description: "Using fear to sway someone to accept a claim or argument.",
    examples: ["If we don't increase military spending, we'll be vulnerable to attack.", "Vote for me or the economy will collapse."],
    antidote: "Evaluate the evidence and logic independently of the fear being evoked.",
    category: "Appeal",
    severity: "Medium",
  },
  "appeal-to-popularity": {
    name: "Appeal to Popularity",
    alternativeNames: ["Argument from Popularity", "Bandwagon Fallacy"],
    description: "Asserting that something is true because many people believe it.",
    examples: ["Everyone is buying this new phone, so it must be the best.", "Millions of people believe this, so it must be true."],
    antidote: "Recognize that popularity does not equate to truth or validity.",
    category: "Appeal",
    severity: "Low",
  },
  "attacking-the-person": {
    name: "Attacking the Person",
    alternativeNames: ["Ad Hominem", "Personal Attack"],
    description: "Attacking the person making the argument instead of the argument itself.",
    examples: ["You can't trust John's opinion on climate change; he's not a scientist.", "She's just saying that because she's young and naive."],
    antidote: "Focus on the argument's merits, not the person making it.",
    category: "Informal",
    severity: "High",
  },
  "bandwagon-fallacy": {
    name: "Bandwagon Fallacy",
    alternativeNames: ["Appeal to the Masses", "Argumentum ad Populum"],
    description: "Arguing that because something is popular, it is therefore good, correct, or desirable.",
    examples: ["Everyone is doing it, so it must be the right thing to do.", "This restaurant is always packed, so the food must be great."],
    antidote: "Consider the evidence and merits of the idea, regardless of its popularity.",
    category: "Appeal",
    severity: "Low",
  },
  "begging-the-question": {
    name: "Begging the Question",
    alternativeNames: ["Circular Reasoning"],
    description: "Assuming the conclusion in the premise of the argument.",
    examples: ["God exists because the Bible says so, and the Bible is the word of God.", "I'm trustworthy because I always tell the truth."],
    antidote: "Identify the premise that assumes the conclusion and challenge its validity.",
    category: "Formal",
    severity: "High",
  },
  "burden-of-proof": {
    name: "Burden of Proof",
    alternativeNames: ["Shifting the Burden of Proof"],
    description: "Placing the responsibility of disproving a claim on the person making the claim.",
    examples: ["You can't prove that aliens haven't visited Earth, so they must have.", "Show me evidence that my product doesn't work."],
    antidote: "Remind the claimant that they have the initial responsibility to provide evidence.",
    category: "Informal",
    severity: "Medium",
  },
  "cause-and-effect": {
    name: "Cause and Effect",
    alternativeNames: ["False Cause", "Post Hoc Ergo Propter Hoc"],
    description: "Assuming that because one event followed another, the first event caused the second.",
    examples: ["After I started taking this supplement, my hair grew faster. Therefore, the supplement caused my hair growth.", "The rooster crows before sunrise, so the rooster causes the sun to rise."],
    antidote: "Look for other possible causes and consider the possibility of coincidence.",
    category: "Causation",
    severity: "Medium",
  },
  "circular-reasoning": {
    name: "Circular Reasoning",
    alternativeNames: ["Begging the Question"],
    description: "Using the conclusion to support the premise, and the premise to support the conclusion.",
    examples: ["The Bible is true because it is the word of God, and we know God exists because the Bible says so.", "This law is just because it's fair, and it's fair because it's just."],
    antidote: "Break down the argument to show how the conclusion relies on the premise and vice versa.",
    category: "Formal",
    severity: "High",
  },
  "composition-fallacy": {
    name: "Composition Fallacy",
    alternativeNames: ["Fallacy of Composition"],
    description: "Assuming that what is true of the parts is also true of the whole.",
    examples: ["Each player on the team is excellent, so the team must be excellent.", "This car has the best parts, so it must be the best car overall."],
    antidote: "Consider whether the characteristics of the parts necessarily apply to the whole.",
    category: "Ambiguity",
    severity: "Medium",
  },
  "confirmation-bias": {
    name: "Confirmation Bias",
    alternativeNames: ["Cherry Picking"],
    description: "Seeking out or interpreting evidence in a way that confirms one's pre-existing beliefs or hypotheses.",
    examples: ["Only reading news articles that support your political views.", "Remembering only the times your horoscope was accurate."],
    antidote: "Actively seek out and consider evidence that contradicts your beliefs.",
    category: "Informal",
    severity: "Medium",
  },
  "correlation-causation": {
    name: "Correlation/Causation",
    alternativeNames: ["Correlation Does Not Imply Causation"],
    description: "Assuming that because two things are correlated, one caused the other.",
    examples: ["Ice cream sales and crime rates are correlated, so ice cream causes crime.", "People who exercise more live longer, so exercise alone causes longevity."],
    antidote: "Consider other possible causes and the possibility of coincidence.",
    category: "Causation",
    severity: "Medium",
  },
  "division-fallacy": {
    name: "Division Fallacy",
    alternativeNames: ["Fallacy of Division"],
    description: "Assuming that what is true of the whole is also true of the parts.",
    examples: ["The company is successful, so every employee must be successful.", "This country is wealthy, so everyone in it must be rich."],
    antidote: "Consider whether the characteristics of the whole necessarily apply to the parts.",
    category: "Ambiguity",
    severity: "Medium",
  },
  "equivocation-fallacy": {
    name: "Equivocation Fallacy",
    alternativeNames: ["Doublespeak", "Semantic Slippery Slope"],
    description: "Using a word or phrase in different senses within the same argument.",
    examples: ["The sign said 'fine for parking here', and since it was fine to park there, I parked there.", "A feather is light. What is light cannot be dark. Therefore, a feather cannot be dark."],
    antidote: "Identify the different meanings of the word and clarify which meaning is intended.",
    category: "Ambiguity",
    severity: "Medium",
  },
  "false-dilemma": {
    name: "False Dilemma",
    alternativeNames: ["Either/Or Fallacy", "False Dichotomy"],
    description: "Presenting only two options when more exist.",
    examples: ["You're either with us, or you're against us.", "Either we cut all social programs or the economy will fail."],
    antidote: "Identify other possible options and challenge the limited choices presented.",
    category: "Informal",
    severity: "Medium",
  },
  "hasty-generalization": {
    name: "Hasty Generalization",
    alternativeNames: ["Overgeneralization"],
    description: "Drawing a conclusion based on insufficient evidence.",
    examples: ["I met two rude people from that city, so everyone from that city must be rude.", "My neighbor's dog bit me, so all dogs are dangerous."],
    antidote: "Gather more evidence and consider whether the sample is representative.",
    category: "Informal",
    severity: "Medium",
  },
  "ignoring-the-question": {
    name: "Ignoring the Question",
    alternativeNames: ["Red Herring", "Irrelevant Conclusion"],
    description: "Introducing an irrelevant topic to divert attention from the main issue.",
    examples: ["You criticize my environmental policies, but what about the economy?", "Sure, I lied, but nobody's perfect."],
    antidote: "Refocus the discussion on the original issue and point out the irrelevance.",
    category: "Informal",
    severity: "Medium",
  },
  "loaded-question": {
    name: "Loaded Question",
    alternativeNames: ["Complex Question", "Trick Question"],
    description: "Asking a question that contains a presupposition.",
    examples: ["Have you stopped beating your wife?", "Why do you always lie about your accomplishments?"],
    antidote: "Challenge the presupposition and refuse to answer the question directly.",
    category: "Informal",
    severity: "Medium",
  },
  "missing-the-point": {
    name: "Missing the Point",
    alternativeNames: ["Irrelevant Conclusion"],
    description: "Drawing a conclusion that is not logically related to the evidence presented.",
    examples: ["Crime has increased, so we should reduce taxes.", "The movie had great special effects, therefore it deserves best picture."],
    antidote: "Explain how the conclusion does not follow from the evidence.",
    category: "Informal",
    severity: "Medium",
  },
  "moral-equivalence": {
    name: "Moral Equivalence",
    alternativeNames: ["False Equivalence"],
    description: "Comparing minor misdeeds with major atrocities.",
    examples: ["Parking tickets are just as bad as genocide.", "Getting a bad grade is like being tortured."],
    antidote: "Point out the significant differences in severity and impact.",
    category: "Informal",
    severity: "High",
  },
  "non-sequitur": {
    name: "Non Sequitur",
    alternativeNames: ["It Does Not Follow"],
    description: "Drawing a conclusion that does not logically follow from the premises.",
    examples: ["I like pizza, so I must be good at math.", "The sky is blue, therefore democracy is the best form of government."],
    antidote: "Explain why the conclusion does not follow from the premises.",
    category: "Formal",
    severity: "High",
  },
  "perfectionist-fallacy": {
    name: "Perfectionist Fallacy",
    alternativeNames: ["Nirvana Fallacy"],
    description: "Rejecting a solution because it is not perfect.",
    examples: ["Why bother with bike lanes? Cars are still faster.", "This charity only helps 80% of people in need, so it's useless."],
    antidote: "Compare the proposed solution to the alternative, not to a perfect ideal.",
    category: "Informal",
    severity: "Low",
  },
  "post-hoc": {
    name: "Post Hoc",
    alternativeNames: ["False Cause"],
    description: "Assuming that because one event followed another, the first event caused the second.",
    examples: ["I wore my lucky socks, and we won the game. Therefore, my socks caused us to win.", "I washed my car and then it rained, so washing my car causes rain."],
    antidote: "Look for other possible causes and consider the possibility of coincidence.",
    category: "Causation",
    severity: "Medium",
  },
  "red-herring": {
    name: "Red Herring",
    alternativeNames: ["Ignoring the Question"],
    description: "Introducing an irrelevant topic to divert attention from the main issue.",
    examples: ["You criticize my environmental policies, but what about the economy?", "I may have cheated on the test, but I studied really hard last semester."],
    antidote: "Refocus the discussion on the original issue and point out the irrelevance.",
    category: "Informal",
    severity: "Medium",
  },
  "slippery-slope": {
    name: "Slippery Slope",
    alternativeNames: ["Chain Reaction Fallacy"],
    description: "Asserting that one event will inevitably lead to a series of negative consequences.",
    examples: ["If we legalize marijuana, then everyone will start using heroin.", "If we allow students to redo tests, next they'll want to redo entire courses."],
    antidote: "Challenge the assumption that one event will inevitably lead to the others.",
    category: "Informal",
    severity: "Medium",
  },
  "straw-man": {
    name: "Straw Man",
    alternativeNames: ["Misrepresentation"],
    description: "Misrepresenting someone's argument to make it easier to attack.",
    examples: ["My opponent wants to cut military spending, so he must want to leave us defenseless.", "You want to reform healthcare, so you must want socialism."],
    antidote: "Accurately represent the original argument and address it directly.",
    category: "Informal",
    severity: "High",
  },
  "anecdotal-evidence": {
    name: "Anecdotal Evidence",
    alternativeNames: ["Personal Experience"],
    description: "Using personal experience or isolated examples instead of sound reasoning or compelling evidence.",
    examples: ["I smoked for 40 years and I'm fine, so smoking can't be that bad for you.", "My friend got rich using this method, so it must work for everyone."],
    antidote: "Recognize that personal experiences are not a substitute for scientific evidence.",
    category: "Informal",
    severity: "Medium",
  },
  "appeal-to-ignorance": {
    name: "Appeal to Ignorance",
    alternativeNames: ["Argument from Ignorance"],
    description: "Arguing that a claim is true simply because it has not been proven false (or that a claim is false simply because it has not been proven true).",
    examples: ["No one has proven that ghosts don't exist, therefore they must exist.", "There's no evidence that this treatment doesn't work, so it must be effective."],
    antidote: "Understand that a lack of evidence is not evidence of absence.",
    category: "Appeal",
    severity: "Medium",
  },
  "cherry-picking": {
    name: "Cherry Picking",
    alternativeNames: ["Suppressed Evidence"],
    description: "Selectively presenting only the data that supports your argument while ignoring contradictory evidence.",
    examples: ["Citing crime statistics that show a decrease in violent crime while ignoring an increase in property crime.", "Only mentioning positive customer reviews while hiding negative ones."],
    antidote: "Consider the totality of the evidence and acknowledge any contradictory data.",
    category: "Informal",
    severity: "Medium",
  },
  "false-analogy": {
    name: "False Analogy",
    alternativeNames: ["Weak Analogy"],
    description: "Drawing a comparison between two things that are not alike in the relevant respects.",
    examples: ["Saying that employees are like nails; just as nails must be hit in the head to make them work, so must employees.", "The universe is like a watch, so it must have been designed by a watchmaker."],
    antidote: "Identify the dissimilarities between the two things being compared.",
    category: "Ambiguity",
    severity: "Medium",
  },
  "genetic-fallacy": {
    name: "Genetic Fallacy",
    alternativeNames: ["Argument from Origins"],
    description: "Rejecting an argument based on its source or origin, rather than its merits.",
    examples: ["That idea came from a communist, so it must be bad.", "This product was made in that country, so it must be low quality."],
    antidote: "Evaluate the argument based on its own merits, regardless of its source.",
    category: "Informal",
    severity: "Medium",
  },
  "middle-ground-fallacy": {
    name: "Middle Ground Fallacy",
    alternativeNames: ["Argument from Compromise"],
    description: "Assuming that the truth lies in the middle of two opposing positions.",
    examples: ["One person says the sky is blue, another says it's black, so the truth must be that it's gray.", "Some say vaccines are safe, others say they're deadly, so the truth must be somewhere in between."],
    antidote: "Evaluate the evidence and merits of each position independently.",
    category: "Informal",
    severity: "Low",
  },
  "moving-the-goalpost": {
    name: "Moving the Goalpost",
    alternativeNames: ["Shifting Goalposts"],
    description: "Changing the criteria for success or proof mid-argument to make it impossible for the opponent to win.",
    examples: ["Demanding more and more evidence after each piece of evidence is presented.", "You said you'd prove it with one study, but now that you have, I need five more."],
    antidote: "Point out that the criteria for success have been unfairly changed.",
    category: "Informal",
    severity: "Medium",
  },
  "no-true-scotsman": {
    name: "No True Scotsman",
    alternativeNames: ["Appeal to Purity"],
    description: "Making an appeal to purity as a way to dismiss relevant criticisms or flaws of an argument.",
    examples: ["Saying that 'no true Scotsman would put sugar on his porridge' after someone points out that a Scotsman does.", "No true Christian would do that, dismissing counterexamples."],
    antidote: "Challenge the arbitrary redefinition of the group or category.",
    category: "Informal",
    severity: "Medium",
  },
  "relative-privation": {
    name: "Relative Privation",
    alternativeNames: ["Appeal to Worse Problems"],
    description: "Dismissing an argument or complaint because there are worse problems in the world.",
    examples: ["Saying that someone shouldn't complain about their job because there are people starving in Africa.", "You can't be sad about your breakup when people are dying of disease."],
    antidote: "Acknowledge that the existence of worse problems does not invalidate the original complaint.",
    category: "Informal",
    severity: "Low",
  },
  "special-pleading": {
    name: "Special Pleading",
    alternativeNames: ["Exception to the Rule"],
    description: "Applying standards, principles, and/or rules to other people or circumstances, while taking oneself (or those one has a special interest in) to be exempt, without providing adequate justification.",
    examples: ["Saying that the rules don't apply to you because you're special.", "Everyone should pay taxes except me because I need the money more."],
    antidote: "Insist on the consistent application of standards and rules.",
    category: "Informal",
    severity: "Medium",
  },
};

export const FALLACY_DEFINITIONS = FALLACY_DATA;

export const FALLACY_CATEGORIES = {
  "Formal": "Logical structure errors",
  "Informal": "Content and context errors", 
  "Appeal": "Emotional or inappropriate appeals",
  "Causation": "Cause and effect errors",
  "Ambiguity": "Language and meaning confusion"
} as const;

export const EXPANDED_FALLACY_TYPES_DATA: FallacyDefinition[] = Object.values(FALLACY_DATA);

export const EXPANDED_FALLACY_TYPES = FALLACY_DATA;

export const FALLACY_IDS = Object.keys(FALLACY_DATA);
